import type { ReactNode } from 'react';

interface RangeFieldProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  unit?: string;
  onChange: (value: number) => void;
}

export function RangeField({ label, value, min, max, step = 1, unit = '', onChange }: RangeFieldProps) {
  const progress = ((value - min) / (max - min)) * 100;
  return (
    <label className="range-field">
      <span className="field-label-row">
        <span>{label}</span>
        <output>{Number.isInteger(value) ? value : value.toFixed(1)}{unit}</output>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        style={{ '--range-progress': `${progress}%` } as React.CSSProperties}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

interface TextFieldProps {
  label: string;
  value: string;
  multiline?: boolean;
  rows?: number;
  maxLength?: number;
  onChange: (value: string) => void;
}

export function TextField({ label, value, multiline = false, rows = 3, maxLength = 180, onChange }: TextFieldProps) {
  const id = `field-${label.replace(/\s+/g, '-').toLowerCase()}`;
  return (
    <label className="text-field" htmlFor={id}>
      <span className="field-label-row">
        <span>{label}</span>
        <span className="character-count">{value.length}/{maxLength}</span>
      </span>
      {multiline ? (
        <textarea id={id} value={value} rows={rows} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} />
      ) : (
        <input id={id} type="text" value={value} maxLength={maxLength} onChange={(event) => onChange(event.target.value)} />
      )}
    </label>
  );
}

interface SelectFieldProps<T extends string> {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (value: T) => void;
}

export function SelectField<T extends string>({ label, value, options, onChange }: SelectFieldProps<T>) {
  return (
    <label className="select-field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
      </select>
    </label>
  );
}

interface ColorFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
}

export function ColorField({ label, value, onChange }: ColorFieldProps) {
  return (
    <label className="color-field">
      <span>{label}</span>
      <span className="color-input-wrap">
        <input type="color" value={value} onChange={(event) => onChange(event.target.value)} />
        <code>{value.toUpperCase()}</code>
      </span>
    </label>
  );
}

interface ToggleFieldProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (value: boolean) => void;
}

export function ToggleField({ label, description, checked, onChange }: ToggleFieldProps) {
  return (
    <label className="toggle-field">
      <span>
        <strong>{label}</strong>
        {description && <small>{description}</small>}
      </span>
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span className="toggle-track" aria-hidden="true"><span /></span>
    </label>
  );
}

export function InspectorSection({ title, actions, children }: { title: string; actions?: ReactNode; children: ReactNode }) {
  return (
    <section className="inspector-section">
      <header>
        <h2>{title}</h2>
        {actions}
      </header>
      <div className="inspector-section-content">{children}</div>
    </section>
  );
}
